import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Card, Badge, Button, Icon, Toast } from '@atoms/index';
import { orderService } from '@services/orderService';
import { demoService } from '@services/demoService';
import { useOrderSocket } from '@/hooks/useOrderSocket';
import { LiveOrderMap } from '@/components/organisms/LiveOrderMap';
import { OrderSimulationPanel } from '@/components/organisms/OrderSimulationPanel';
import { AlertModal } from '@/components/organisms/AlertModal';
import { CancelOrderModal } from '@/components/organisms/CancelOrderModal';
import OrderSummary from '@/components/organisms/OrderSummary';
import { formatCurrency } from '@utils/currency';
import { formatPaymentMethod, formatOrderStatus, formatPrice, formatPaymentStatus, formatCancellationReason } from '@utils/formatters';
import type { Order, OrderStatus } from '@/types';

const OrderTrackingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [blockingError, setBlockingError] = useState<string | null>(null);
  const [nonBlockingError, setNonBlockingError] = useState<string | null>(null);
  const nonBlockingErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showCancelToast, setShowCancelToast] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelToastType, setCancelToastType] = useState<'success' | 'error' | 'info'>('info');
  const [simulationAlert, setSimulationAlert] = useState<{ isOpen: boolean; title: string; message: string; variant: 'success' | 'danger' }>({ isOpen: false, title: '', message: '', variant: 'success' });
  const [courierLocation, setCourierLocation] = useState<{ 
    lat: number; 
    lng: number; 
    bearing?: number;
    remainingTime?: number;
  } | null>(null);
  const [routePath, setRoutePath] = useState<number[][] | null>(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const animationShownRef = useRef(false);
  const [isStartingSimulation, setIsStartingSimulation] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelSubmitting, setIsCancelSubmitting] = useState(false);

  const { on, off, isConnected, isSubscribed, connectionError } = useOrderSocket(id || null);

  const canCancelOrder =
    order &&
    ['CREATED', 'PENDING', 'SCHEDULED'].includes(order.status || '');

  useEffect(() => {
    if (order) {
      console.log('🔍 OrderTrackingPage state:', {
        orderId: id,
        orderStatus: order.status || 'CREATED',
        courierLocation,
        routePath: routePath ? `${routePath.length} waypoints` : 'null',
        isConnected,
        isSubscribed,
      });
    }
  }, [id, order, courierLocation, routePath, isConnected, isSubscribed]);

  useEffect(() => {
    if (!id) {
      setBlockingError('Geçersiz sipariş ID. Lütfen sipariş numaranızı kontrol edin.');
      setIsLoading(false);
      return;
    }

    loadOrder(id);

    const interval = setInterval(() => {
      if (id) {
        loadOrder(id);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    return () => {
      if (nonBlockingErrorTimeoutRef.current) {
        clearTimeout(nonBlockingErrorTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!order || animationShownRef.current) return;

    const fromState = (location.state as any)?.showSuccessAnimation;
    if (fromState) {
      setShowSuccessAnimation(true);
      animationShownRef.current = true;
      setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 3000);
      return;
    }

    const paymentSuccessKey = `payment_success_${order.id}`;
    const paymentSuccessTime = sessionStorage.getItem(paymentSuccessKey);
    if (paymentSuccessTime) {
      const timeDiff = Date.now() - parseInt(paymentSuccessTime, 10);
      if (timeDiff < 2 * 60 * 1000) {
        setShowSuccessAnimation(true);
        animationShownRef.current = true;
        sessionStorage.removeItem(paymentSuccessKey);
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);
        return;
      } else {
        sessionStorage.removeItem(paymentSuccessKey);
      }
    }

    if (order.createdAt) {
      try {
        const orderDate = new Date(order.createdAt);
        if (!isNaN(orderDate.getTime())) {
          const now = new Date();
          const ageMinutes = (now.getTime() - orderDate.getTime()) / (1000 * 60);
          
          if (ageMinutes < 2 && order.status === 'PREPARING') {
            setShowSuccessAnimation(true);
            animationShownRef.current = true;
            setTimeout(() => {
              setShowSuccessAnimation(false);
            }, 3000);
          }
        }
      } catch (err) {
        console.warn('Invalid order.createdAt date:', order.createdAt, err);
      }
    }
  }, [order, location.state]);

  useEffect(() => {
    if (!isConnected || !isSubscribed || !id) return;

    const handleOrderStatusChanged = (data: { orderId: string; status: OrderStatus; previousStatus?: string }) => {
      if (data.orderId === id) {
        loadOrder(id);

        if (data.status === 'CANCELLED') {
          setCancelMessage('Siparişiniz iptal edildi.');
          setShowCancelToast(true);
        }
      }
    };

    const handleCourierLocation = (data: { 
      orderId: string; 
      latitude: number; 
      longitude: number;
      bearing?: number;
      remainingTime?: number;
    }) => {
      if (data.orderId === id) {
        const latitude = Number(data.latitude);
        const longitude = Number(data.longitude);
        
        console.log('📍 COURIER_LOCATION received:', { 
          latitude, 
          longitude, 
          rawData: data,
          bearing: data.bearing, 
          remainingTime: data.remainingTime 
        });
        
        if (!isNaN(latitude) && !isNaN(longitude) && 
            latitude >= -90 && latitude <= 90 && 
            longitude >= -180 && longitude <= 180) {
          console.log('✅ Setting courier location state:', { 
            lat: latitude, 
            lng: longitude,
            previousLocation: courierLocation,
            bearing: data.bearing !== undefined ? Number(data.bearing) : undefined,
          });
          setCourierLocation({ 
            lat: latitude,
            lng: longitude,
            bearing: data.bearing !== undefined ? Number(data.bearing) : undefined,
            remainingTime: data.remainingTime !== undefined ? Number(data.remainingTime) : undefined,
          });
        } else {
          console.warn('❌ Invalid courier location coordinates:', { latitude, longitude, rawData: data });
        }
      }
    };

    const handleRoutePath = (data: { 
      orderId: string; 
      routePath: number[][];
    }) => {
      if (data.orderId === id) {
        console.log('🗺️ ROUTE_PATH received:', { 
          waypointCount: data.routePath?.length, 
          firstPoint: data.routePath?.[0], 
          lastPoint: data.routePath?.[data.routePath.length - 1],
          firstPointAsLatLng: data.routePath?.[0] ? `lat=${data.routePath[0][0]}, lng=${data.routePath[0][1]}` : 'null',
          lastPointAsLatLng: data.routePath?.[data.routePath.length - 1] ? `lat=${data.routePath[data.routePath.length - 1][0]}, lng=${data.routePath[data.routePath.length - 1][1]}` : 'null',
        });
        setRoutePath(data.routePath);
        console.log('✅ Route path set to state');
      }
    };

    const handlePaymentSuccess = (data: { orderId: string; orderNumber: string }) => {
      if (data.orderId === id) {
        console.log('✅ Payment success received for order:', data.orderNumber);
        loadOrder(id);
      }
    };

    on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    on('COURIER_LOCATION', handleCourierLocation);
    on('ROUTE_PATH', handleRoutePath);
    on('PAYMENT_SUCCESS', handlePaymentSuccess);

    return () => {
      off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      off('COURIER_LOCATION', handleCourierLocation);
      off('ROUTE_PATH', handleRoutePath);
      off('PAYMENT_SUCCESS', handlePaymentSuccess);
    };
  }, [isConnected, isSubscribed, id, on, off]);

  const loadOrder = async (orderId: string) => {
    if (!orderId) {
      setBlockingError('Sipariş ID bulunamadı.');
      setIsLoading(false);
      return;
    }

    try {
      const isInitialLoad = !order;
      if (isInitialLoad) {
        setIsLoading(true);
      }

      setBlockingError(null);

      console.log('🔍 Loading order:', orderId);
      const data = await orderService.getOrder(orderId);

      console.log('✅ Order Data Received (frontend):', data);

      if (!data || !data.id) {
        console.error('❌ Invalid order data: missing id or data is null', data);
        if (!order) {
          setBlockingError('Sipariş verisi geçersiz.');
          setOrder(null);
        } else {
          setNonBlockingError('Sipariş verisi geçici olarak güncellenemedi.');
        }
        return;
      }

      if (!data.restaurantId || !data.userId) {
        console.error('❌ Invalid order data: missing required fields', data);
        if (!order) {
          setBlockingError('Sipariş verisi eksik. Lütfen sayfayı yenileyin.');
          setOrder(null);
        } else {
          setNonBlockingError('Sipariş verisi geçici olarak güncellenemedi.');
        }
        return;
      }

      if (!Array.isArray(data.items)) {
        console.warn('⚠️ Order items is not an array, defaulting to empty array', data.items);
        data.items = [];
      }
      
      setOrder(data);
      setBlockingError(null);
      setNonBlockingError(null);
      console.log('✅ Order set successfully:', data.id);
    } catch (err: any) {
      console.error('❌ Load order error:', err);
      console.error('Error stack:', err.stack);
      console.error('Error response:', err.response);
      
      const errorMessage = err.response?.data?.error?.message || err.message || 'Sipariş bilgileri yüklenirken bir hata oluştu.';
      if (!order) {
        setBlockingError(errorMessage);
        setOrder(null);
      } else {
        setNonBlockingError(errorMessage);
        if (nonBlockingErrorTimeoutRef.current) {
          clearTimeout(nonBlockingErrorTimeoutRef.current);
        }
        nonBlockingErrorTimeoutRef.current = setTimeout(() => {
          setNonBlockingError(null);
        }, 5000);
      }
    } finally {
      if (!order) {
        setIsLoading(false);
      }
    }
  };

  const getStatusInfo = (status: OrderStatus) => {
    const formattedLabel = formatOrderStatus(status);
    const statusMap: Partial<Record<OrderStatus, { variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }>> = {
      CREATED: { variant: 'default' },
      SCHEDULED: { variant: 'info' },
      PENDING: { variant: 'warning' },
      PREPARING: { variant: 'warning' },
      READY: { variant: 'info' },
      ON_WAY: { variant: 'info' },
      DELIVERED: { variant: 'success' },
      CANCELLED: { variant: 'danger' },
    };
    return { label: formattedLabel, variant: statusMap[status]?.variant || 'default' };
  };

  const getStatusSteps = (currentStatus: OrderStatus) => {
    const steps = [
      { status: 'CREATED' as OrderStatus, label: 'Sipariş Alındı', icon: 'check' },
      { status: 'PREPARING' as OrderStatus, label: 'Hazırlanıyor', icon: 'clock' },
      { status: 'READY' as OrderStatus, label: 'Hazır', icon: 'check' },
      { status: 'ON_WAY' as OrderStatus, label: 'Yolda', icon: 'location' },
      { status: 'DELIVERED' as OrderStatus, label: 'Teslim Edildi', icon: 'check' },
    ];

    const statusOrder = ['CREATED', 'SCHEDULED', 'PENDING', 'PREPARING', 'READY', 'ON_WAY', 'DELIVERED'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    // Map status to step index
    const statusToStepMap: Record<string, number> = {
      CREATED: 0,
      SCHEDULED: 0,
      PENDING: 0,
      PREPARING: 1,
      READY: 2,
      ON_WAY: 3,
      DELIVERED: 4,
    };
    const stepIndex = statusToStepMap[currentStatus] ?? 0;

    return steps.map((step, index) => ({
      ...step,
      isCompleted: index <= stepIndex,
      isCurrent: index === stepIndex,
    }));
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="mt-4 text-gray-600 text-lg">Siparişiniz yükleniyor...</p>
            <p className="mt-2 text-sm text-gray-500">Lütfen bekleyin</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (blockingError || !order) {
    return (
      <MainLayout>
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-4">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-red-600 text-lg mb-4 font-medium">
              {blockingError || 'Sipariş bulunamadı'}
            </p>
            <p className="text-gray-600 mb-6">
              {!id
                ? 'Geçersiz sipariş ID. Lütfen sipariş numaranızı kontrol edin.'
                : 'Sipariş bilgileri yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'}
            </p>
            <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!order || !order.id) {
    return (
      <MainLayout>
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-4">
            <p className="text-red-600 text-lg mb-4 font-medium">Sipariş verisi geçersiz</p>
            <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const deliveryAddress = order.deliveryAddress || null;
  const deliveryLat = (order as any).deliveryLatitude ?? deliveryAddress?.latitude;
  const deliveryLng = (order as any).deliveryLongitude ?? deliveryAddress?.longitude;
  const restaurant = (order as any).restaurant;
  const restaurantLat = restaurant?.latitude ? Number(restaurant.latitude) : null;
  const restaurantLng = restaurant?.longitude ? Number(restaurant.longitude) : null;
  const hasValidCoords = 
    deliveryLat != null && 
    deliveryLng != null && 
    !isNaN(Number(deliveryLat)) && 
    !isNaN(Number(deliveryLng)) &&
    Number(deliveryLat) >= -90 && 
    Number(deliveryLat) <= 90 &&
    Number(deliveryLng) >= -180 && 
    Number(deliveryLng) <= 180 &&
    !(Number(deliveryLat) === 0 && Number(deliveryLng) === 0);
  const hasRestaurantCoords = 
    restaurantLat != null && 
    restaurantLng != null && 
    !isNaN(restaurantLat) && 
    !isNaN(restaurantLng) &&
    restaurantLat >= -90 && 
    restaurantLat <= 90 &&
    restaurantLng >= -180 && 
    restaurantLng <= 180;

  const orderItems = Array.isArray(order.items) ? order.items : [];
  const orderStatus = order.status || 'CREATED';

  const statusInfo = getStatusInfo(orderStatus as OrderStatus);
  const steps = getStatusSteps(orderStatus as OrderStatus);

  // Demo: Start simulation handler
  const handleStartSimulation = async () => {
    if (!order?.id) return;
    
    try {
      setIsStartingSimulation(true);
      const result = await demoService.startSimulation(order.id);
      console.log('✅ Simulation started:', result);
      
      setSimulationAlert({
        isOpen: true,
        title: 'Simülasyon başlatıldı',
        message: `Süre: ${Math.ceil(result.durationSeconds / 60)} dakika`,
        variant: 'success',
      });
      await loadOrder(order.id);
    } catch (error: any) {
      console.error('❌ Failed to start simulation:', error);
      setSimulationAlert({
        isOpen: true,
        title: 'Simülasyon başlatılamadı',
        message: error.message || 'Bilinmeyen hata',
        variant: 'danger',
      });
    } finally {
      setIsStartingSimulation(false);
    }
  };

  const handleConfirmCancel = async (reason?: string) => {
    if (!order?.id) return;
    try {
      setIsCancelSubmitting(true);
      await orderService.cancelOrder(order.id, reason);
      setOrder((prev) => (prev ? { ...prev, status: 'CANCELLED' } : null));
      setShowCancelModal(false);
      setCancelMessage(
        order.paymentMethod === 'CREDIT_CARD'
          ? 'Siparişiniz iptal edildi. İade 5–10 iş günü içinde kartınıza yansıyacaktır.'
          : 'Siparişiniz iptal edildi.'
      );
      setCancelToastType('success');
      setShowCancelToast(true);
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.message || 'Sipariş iptal edilemedi.';
      setNonBlockingError(message);
    } finally {
      setIsCancelSubmitting(false);
    }
  };

  const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';

  return (
    <MainLayout>
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center animate-bounce">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Başarılı!</h2>
            <p className="text-gray-600">Siparişiniz alındı ve hazırlanmaya başlandı.</p>
          </div>
        </div>
      )}

      <Toast
        message={cancelMessage}
        type={cancelToastType}
        isVisible={showCancelToast}
        onClose={() => setShowCancelToast(false)}
        duration={7000}
      />

      <Toast
        message={nonBlockingError || ''}
        type="warning"
        isVisible={Boolean(nonBlockingError)}
        onClose={() => setNonBlockingError(null)}
        duration={5000}
      />

      <AlertModal
        isOpen={simulationAlert.isOpen}
        title={simulationAlert.title}
        message={simulationAlert.message}
        variant={simulationAlert.variant}
        onClose={() => setSimulationAlert((a) => ({ ...a, isOpen: false }))}
      />

      {order && (
        <CancelOrderModal
          isOpen={showCancelModal}
          order={{ ...order, restaurant: (order as any).restaurant }}
          isSubmitting={isCancelSubmitting}
          onConfirm={handleConfirmCancel}
          onClose={() => !isCancelSubmitting && setShowCancelModal(false)}
        />
      )}

      <div className="max-w-4xl mx-auto">
        <nav className="mb-4 text-sm">
          <Link to="/orders" className="text-primary-600 hover:underline">
            Siparişlerim
          </Link>
          <span className="mx-2 text-gray-400">›</span>
          <span className="text-gray-600">
            {orderStatus === 'DELIVERED' || orderStatus === 'CANCELLED' ? 'Sipariş Detayları' : 'Sipariş Takibi'}
          </span>
        </nav>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {orderStatus === 'DELIVERED' || orderStatus === 'CANCELLED' ? 'Sipariş Detayları' : 'Sipariş Takibi'}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-gray-500">
                Sipariş No: {order.orderNumber || (order.id ? `#${order.id.slice(0, 8)}` : 'N/A')}
              </span>
              <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              {canCancelOrder && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCancelModal(true)}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                >
                  Siparişi iptal et
                </Button>
              )}
            </div>
          </div>
        </div>

        {orderStatus === 'CANCELLED' && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="close" size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 text-lg mb-1">Sipariş İptal Edildi</h3>
                {(order.cancellationReason || (order as any).cancellation_reason) && (
                  <div className="mb-3">
                    <p className="text-sm text-red-700 font-medium mb-0.5">İptal Sebebi</p>
                    <p className="text-sm text-red-900 bg-red-100 rounded-lg px-3 py-2 border border-red-200">
                      {formatCancellationReason(order.cancellationReason || (order as any).cancellation_reason)}
                    </p>
                  </div>
                )}
                {(order.cancelledAt || (order as any).cancelled_at) && (
                  <p className="text-xs text-red-600 mb-3">
                    İptal tarihi:{' '}
                    {new Date(order.cancelledAt || (order as any).cancelled_at).toLocaleString('tr-TR')}
                  </p>
                )}
                {order.paymentMethod === 'CREDIT_CARD' && (
                  <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-red-200">
                    <span className="text-lg flex-shrink-0">💳</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800">
                          {order.paymentStatus === 'REFUNDED'
                            ? 'İade Tamamlandı'
                            : 'İade İşlemi Başlatıldı'}
                        </p>
                        {(order.refundAmount != null || (order as any).refund_amount != null) && (
                          <span className="text-sm font-bold text-[#2A9D8F]">
                            {formatCurrency(order.refundAmount ?? (order as any).refund_amount)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {order.paymentStatus === 'REFUNDED'
                          ? `${formatCurrency(order.refundAmount ?? order.total)} tutarındaki ödemeniz kartınıza iade edilmiştir.`
                          : `${formatCurrency(order.refundAmount ?? order.total)} tutarındaki ödemeniz 5–10 iş günü içinde kartınıza iade edilecektir.`}
                      </p>
                      {(order.refundedAt || (order as any).refunded_at) && (
                        <p className="text-xs text-[#2A9D8F] font-medium mt-1">
                          İade tarihi: {new Date(order.refundedAt || (order as any).refunded_at).toLocaleString('tr-TR')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {order.paymentMethod === 'CASH' && (
                  <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-red-200">
                    <span className="text-lg flex-shrink-0">💵</span>
                    <p className="text-sm text-gray-700">
                      Kapıda ödeme yöntemiyle verilen bu sipariş için herhangi bir ödeme tahsil edilmemiştir.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {orderStatus !== 'CANCELLED' && (
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.status} className="flex-1 flex items-center">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                        step.isCompleted
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Icon name={step.icon as 'check' | 'clock' | 'location'} size={24} />
                    </div>
                    <p
                      className={`text-sm text-center ${
                        step.isCompleted ? 'text-gray-900 font-medium' : 'text-gray-400'
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        step.isCompleted ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {connectionError && (
          <Toast
            message={connectionError}
            type="warning"
            isVisible={true}
            onClose={() => {}}
            duration={5000}
          />
        )}

        <OrderSimulationPanel
          orderId={order.id}
          currentStatus={orderStatus}
          onStatusUpdate={() => {
            // Reload order after status update
            if (id) {
              loadOrder(id);
            }
          }}
        />

        {isDevelopment && (
          <Card className="mb-6 border-2 border-yellow-400 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-yellow-800 mb-1">🔧 Hata Ayıklama Araçları</h3>
                <p className="text-sm text-yellow-700">
                  Demo amaçlı kurye simülasyonunu manuel olarak başlat
                </p>
              </div>
              <Button
                onClick={handleStartSimulation}
                disabled={isStartingSimulation}
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                {isStartingSimulation ? 'Başlatılıyor...' : '🚀 Simülasyonu Başlat'}
              </Button>
            </div>
          </Card>
        )}

        {orderStatus !== 'CANCELLED' && hasValidCoords ? (
          <div className="mb-6">
            <LiveOrderMap
              restaurantLat={hasRestaurantCoords ? restaurantLat! : Number(deliveryLat) - 0.01}
              restaurantLng={hasRestaurantCoords ? restaurantLng! : Number(deliveryLng) - 0.01}
              deliveryLat={Number(deliveryLat)}
              deliveryLng={Number(deliveryLng)}
              courierLat={courierLocation?.lat || null}
              courierLng={courierLocation?.lng || null}
              courierBearing={courierLocation?.bearing || null}
              courierRemainingTime={courierLocation?.remainingTime || null}
              routePath={routePath}
              orderStatus={orderStatus}
            />
          </div>
        ) : orderStatus !== 'CANCELLED' ? (
          <div className="mb-6">
            <Card className="p-10 text-center bg-gray-50">
              <Icon name="location" size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-2">Harita kullanılamıyor</p>
              <p className="text-sm text-gray-500">
                Bu adres için konum bilgisi bulunamadı. Siparişiniz normal şekilde teslim edilecektir.
              </p>
            </Card>
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {(order as any).restaurant && (
              <Card>
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Restoran Bilgileri
                </h2>
                <div className="flex items-start gap-4">
                  {(order as any).restaurant.logoUrl && (
                    <img
                      src={(order as any).restaurant.logoUrl}
                      alt={(order as any).restaurant.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {(order as any).restaurant.name}
                    </h3>
                    {(order as any).restaurant.address && (
                      <p className="text-sm text-gray-600 mb-2">
                        {(order as any).restaurant.address}
                      </p>
                    )}
                    {(order as any).restaurant.phone && (
                      <p className="text-sm text-gray-600">
                        📞 {(order as any).restaurant.phone}
                      </p>
                    )}
                    {(() => {
                      const rating = Number((order as any).restaurant?.rating) || 0;
                      const reviewCount = Number((order as any).restaurant?.reviewCount) || 0;
                      
                      if (rating > 0) {
                        return (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-yellow-500">⭐</span>
                            <span className="text-sm font-medium text-gray-700">
                              {rating.toFixed(1)}
                            </span>
                            {reviewCount > 0 && (
                              <span className="text-xs text-gray-500">
                                ({reviewCount} değerlendirme)
                              </span>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </Card>
            )}

            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Sipariş Detayları
              </h2>
              <div className="space-y-3">
                {orderItems.length > 0 ? (
                  orderItems.map((item, index) => {
                    const quantity = Number(item.quantity) || 0;
                    const unitPrice = Number(item.unitPrice) || 0;
                    const itemName = item.menuItemName || 'Ürün';
                    
                    return (
                      <div
                        key={index}
                        className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {quantity}x {itemName}
                          </p>
                          {item.menuItemDescription && (
                            <p className="text-sm text-gray-500">
                              {item.menuItemDescription}
                            </p>
                          )}
                        </div>
                        <p className="font-medium text-gray-900">
                          {formatPrice(unitPrice * quantity)}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-4">Sipariş öğeleri yükleniyor...</p>
                )}
              </div>
            </Card>

            {/* Delivery Address */}
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Teslimat Adresi
              </h2>
              {deliveryAddress ? (
                <div className="flex items-start gap-3">
                  <Icon name="location" size={20} className="text-primary-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900 mb-1">
                      {deliveryAddress.label || 'Teslimat Adresi'}
                    </p>
                    <p className="text-gray-600">
                      {deliveryAddress.fullAddress || (order as any).deliveryAddressText || 'Adres bilgisi yükleniyor...'}
                    </p>
                    {deliveryAddress.building && (
                      <p className="text-sm text-gray-500">
                        Bina: {deliveryAddress.building}
                        {deliveryAddress.floor && `, Kat: ${deliveryAddress.floor}`}
                        {deliveryAddress.apartment && `, Daire: ${deliveryAddress.apartment}`}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <Icon name="location" size={20} className="text-primary-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900 mb-1">Teslimat Adresi</p>
                    <p className="text-gray-600">
                      {(order as any).deliveryAddressText || 'Adres bilgisi yükleniyor...'}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <OrderSummary
              title="Sipariş Özeti"
              variant="detail"
              items={orderItems.map((item) => ({
                name: item.menuItemName || 'Ürün',
                quantity: Number(item.quantity) || 0,
                unitPrice: Number(item.unitPrice) || 0,
                optionNames: item.optionNames,
                removedIngredientNames: item.removedIngredientNames,
                specialInstructions: item.specialInstructions,
              }))}
              subtotal={Number(order.subtotal) || 0}
              deliveryFee={Number(order.deliveryFee) || 0}
              serviceFee={Number(order.serviceFee) || 0}
              promotionDiscount={(order as any).promotionDiscount}
              appliedPromotionName={(order as any).appliedPromotionName}
              total={Number(order.total) || 0}
            />
            <Card>
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex justify-between">
                  <span>Ödeme:</span>
                  <span className="font-medium">
                    {formatPaymentMethod(order.paymentMethod || 'CREDIT_CARD')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Ödeme Durumu:</span>
                  <Badge variant={order.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                    {formatPaymentStatus(order.paymentStatus || 'PENDING')}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>Sipariş Durumu:</span>
                  <Badge variant={statusInfo.variant}>
                    {formatOrderStatus(orderStatus)}
                  </Badge>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderTrackingPage;
